<script setup lang="ts">
/**
 * LandingHero — 랜딩(WS-00) 히어로 섹션. 퍼블리싱 `Hero` + `HeroPreview`(landing-sections.jsx) 이식.
 *
 *  - 비로그인(full): 배경 키비주얼(`/hero-bg.webp`) 위에 아이브로·헤드라인·서브카피·
 *    [시작하기]·[둘러보기]·면책 노트. 텍스트는 좌측 컬럼에만 두고 우측은 배경 인물·그래픽이
 *    드러나도록 비운다(키비주얼의 피사체가 우측에 몰려 있음).
 *    ⚠️ 이미지는 **이 레이아웃에 맞춰 생성한 전용 에셋**(1896×830, 좌측 40% 여백)이다 —
 *    교체할 때 비율이 달라지면 `.ws-hero-in` 의 `min-height`(현재 43.78vw) 도 함께 맞춰야
 *    `cover` 가 세로를 잘라낸다.
 *  - 로그인(compact): "이어서 하기" 아이브로 + 일반 인사 헤드라인(이름 미사용 — auth store 에
 *    표시명 없음, research R5. 퍼블리싱의 "성적 저장됨" 서술도 데이터 비의존 문구로 조정) +
 *    [성적 분석 이어서 하기]·[기능 둘러보기]. 배경 키비주얼 미적용(축소형이라 높이가 부족).
 *
 * 이동·게이트 분기는 페이지(AnalysisHomePage)가 단일 소유 — 여기서는 emit 만(SSoT).
 * 스타일은 퍼블리싱 화면 CSS(ws-hero)를 scoped 이식 — 토큰 참조 유지, 명조 미사용(R3).
 *
 * 배경 이미지가 그린 계열이라 흰색→투명 그라데이션 오버레이(.ws-hero-veil)로 좌측 텍스트 영역의
 * 대비를 확보하고 전체 톤을 완화한다 — 텍스트·버튼 색은 토큰 그대로(Navy+Gold) 유지.
 */
import BaseButton from '#/components/ui/BaseButton.vue';

interface Props {
  /** 로그인 여부 — true 면 축소형(compact) 히어로. */
  authed: boolean;
  /** [시작하기] 처리 중(성적 보유 조회) — 재클릭 차단. */
  pending: boolean;
}

defineProps<Props>();

defineEmits<{
  /** [시작하기]/[성적 분석 이어서 하기] — 페이지가 게이트 판정 후 성적 분석(정시)으로 이동. */
  start: [];
  /** [둘러보기]/[기능 둘러보기] — 페이지가 5축 섹션(#axes)으로 스크롤. */
  browse: [];
}>();
</script>

<template>
  <!-- 로그인: 축소형 히어로 -->
  <section v-if="authed" class="ws-hero ws-hero--compact">
    <div class="wrap ws-hero-compact-in">
      <div>
        <p class="ws-eyebrow">이어서 하기</p>
        <h1 class="ws-hero-title ws-hero-title--sm">성적 분석까지 한 걸음 남았어요</h1>
        <p class="ws-hero-sub">
          수능·내신 성적만 입력하면 내 점수로 본 지원 범위를 확인할 수 있습니다.
        </p>
      </div>
      <div class="ws-hero-actions">
        <BaseButton size="lg" :disabled="pending" @click="$emit('start')">
          성적 분석 이어서 하기
        </BaseButton>
        <BaseButton size="lg" variant="line" @click="$emit('browse')">기능 둘러보기</BaseButton>
      </div>
    </div>
  </section>

  <!-- 비로그인: 전체 히어로(배경 키비주얼) -->
  <section v-else class="ws-hero ws-hero--visual">
    <div class="ws-hero-veil" aria-hidden="true"></div>
    <div class="wrap ws-hero-in">
      <div class="ws-hero-copy">
        <p class="ws-eyebrow">AI 입시 설계</p>
        <h1 class="ws-hero-title">정보는 이미 공개돼 있습니다.<br />문제는 해석입니다</h1>
        <p class="ws-hero-sub">
          전형 요강도 합격선도 찾아보면 나옵니다. 어려운 건 그 숫자가 내 성적에서 무슨 뜻인지입니다.
          성적을 한 번 입력하면 지원 가능 범위, 학과별 해석, 수시 6장 구성까지 여기서 이어집니다.
        </p>
        <div class="ws-hero-actions">
          <BaseButton size="lg" :disabled="pending" @click="$emit('start')">시작하기</BaseButton>
          <BaseButton size="lg" variant="line" @click="$emit('browse')">둘러보기</BaseButton>
        </div>
        <p class="ws-hero-note">
          공개 입시 데이터를 바탕으로 한 참고용 분석입니다. 합격 여부를 보장하지 않습니다.
        </p>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* ── 컨테이너(.wrap) — max-w-page-workspace(1440)·PageShell 좌우 패딩과 동일 값.
   랜딩은 full-bleed 배경이라 scoped 복제(HomePage.vue 관행) — 헤더·푸터 경계와 함께 갱신. ── */
.wrap {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 18px;
}

@media (width >= 600px) {
  .wrap {
    padding: 0 32px;
  }
}

@media (width >= 1080px) {
  .wrap {
    padding: 0 40px;
  }
}

/* ── 히어로 ── */
.ws-hero {
  background: linear-gradient(180deg, var(--bg-2), var(--bg));
  border-bottom: 1px solid var(--line);
}

/* 배경 키비주얼 — full-bleed. 피사체(인물·학사모·정보 카드)가 우측에 몰려 있어 `center right` 로
   고정한다 — 화면이 이미지보다 세로로 길 때 좌우가 잘리더라도 인물이 남고, 좌측 텍스트 영역에는
   원본의 빈 하늘이 오도록 한다. 세로 잘림은 아래 min-height(원본 비율) 가 막는다. */
.ws-hero--visual {
  position: relative;
  isolation: isolate;
  background: var(--bg-2) url('/hero-bg.webp') center right / cover no-repeat;
}

/* 톤 완화 + 좌측 텍스트 대비 확보용 베일. 배경이 그린 계열이라 흰색으로 덮어 채도를 낮춘다. */
.ws-hero-veil {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(
    100deg,
    hsl(0deg 0% 100% / 78%) 0%,
    hsl(0deg 0% 100% / 64%) 34%,
    hsl(0deg 0% 100% / 26%) 62%,
    hsl(0deg 0% 100% / 8%) 100%
  );
}

/* 좁은 화면은 텍스트가 이미지 전면과 겹친다 — 위쪽은 거의 덮어 가독을 확보하고 아래로 갈수록
   열어 키비주얼이 은은히 남게 한다(균일하게 덮으면 피사체가 어정쩡하게 비쳐 지저분하다). */
@media (width < 1080px) {
  .ws-hero-veil {
    background: linear-gradient(
      180deg,
      hsl(0deg 0% 100% / 94%) 0%,
      hsl(0deg 0% 100% / 92%) 78%,
      hsl(0deg 0% 100% / 74%) 100%
    );
  }
}

.ws-hero-in {
  display: grid;
  grid-template-columns: 1fr;
  gap: 36px;
  align-items: center;

  /* 세로만 지정(padding-block) — 좌우 gutter 는 같은 요소의 .wrap 이 소유(shorthand 로 덮으면 소실) */
  padding-block: 56px 60px;
}

@media (width >= 1080px) {
  .ws-hero-in {
    /* 우측 컬럼은 비워 둔다 — 배경 인물·그래픽이 드러나는 자리. */
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
    gap: 56px;
    padding-block: 96px 104px;

    /* 배경 키비주얼 원본 비율(1896×830 = 43.78%)을 히어로 높이로 그대로 쓴다 — 이 값이면
       `cover` 가 세로를 잘라내지 않는다(이미지를 이 비율로 맞춰 생성했다). 상한 760px 은
       세로가 짧은 노트북에서 히어로가 첫 화면을 다 먹지 않게 막는 장치. */
    min-height: min(43.78vw, 760px);
  }
}

/* 배경 위 가독 — 텍스트 폭을 좌측 컬럼 안으로 묶는다. */
.ws-hero-copy {
  max-width: 40em;
}

.ws-eyebrow {
  margin: 0 0 14px;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--accent-deep);
}

.ws-hero-title {
  margin: 0 0 18px;
  font-size: 29px;
  line-height: 1.34;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--brand-ink);
  text-wrap: pretty;
}

@media (width >= 600px) {
  .ws-hero-title {
    font-size: 38px;
    line-height: 1.28;
  }
}

@media (width >= 1080px) {
  .ws-hero-title {
    font-size: 46px;
  }
}

.ws-hero-title--sm {
  font-size: 24px;
  margin-bottom: 10px;
}

@media (width >= 600px) {
  .ws-hero-title--sm {
    font-size: 30px;
  }
}

.ws-hero-sub {
  margin: 0;
  max-width: 34em;
  font-size: 14.5px;
  line-height: 1.78;
  color: var(--body);
  text-wrap: pretty;
}

@media (width >= 600px) {
  .ws-hero-sub {
    font-size: 16px;
  }
}

.ws-hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 30px;
}

@media (width < 600px) {
  .ws-hero-actions > * {
    flex: 1;
  }
}

.ws-hero-note {
  margin: 18px 0 0;
  font-size: 12.5px;
  line-height: 1.7;
  color: var(--muted);
}

/* 축소형(compact) — 로그인 분기 */
.ws-hero--compact {
  background: var(--bg-2);
}

.ws-hero-compact-in {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  flex-wrap: wrap;
  padding-block: 38px;
}

.ws-hero--compact .ws-hero-actions {
  margin-top: 0;
}

.ws-hero--compact .ws-eyebrow {
  margin-bottom: 8px;
}
</style>
